import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface Project {
    id: number;
    projectName: string;
    projectDescription: string;
    createdAt: string;
    updatedAt: string;
    [key: string]: any; // For other potential project properties
}

interface ProjectsState {
    onlineProjects: Project[];
    offlineProjects: Project[];
    isAccordionOpen: boolean;
    activeTab: 'online' | 'offline';
    isLoading: {
        online: boolean;
        offline: boolean;
    };
    error: {
        online: string | null;
        offline: string | null;
    };
}

const initialState: ProjectsState = {
    onlineProjects: [],
    offlineProjects: [],
    isAccordionOpen: false,
    activeTab: 'online',
    isLoading: {
        online: false,
        offline: false
    },
    error: {
        online: null,
        offline: null
    }
};

const projectsSlice = createSlice({
    name: 'projects',
    initialState,
    reducers: {
        setOnlineProjects: (state, action: PayloadAction<Project[]>) => {
            state.onlineProjects = action.payload;
        },
        setOfflineProjects: (state, action: PayloadAction<Project[]>) => {
            state.offlineProjects = action.payload;
        },
        toggleAccordion: (state) => {
            state.isAccordionOpen = !state.isAccordionOpen;
        },
        setActiveTab: (state, action: PayloadAction<'online' | 'offline'>) => {
            state.activeTab = action.payload;
        },
        setLoading: (state, action: PayloadAction<{ type: 'online' | 'offline'; isLoading: boolean }>) => {
            state.isLoading[action.payload.type] = action.payload.isLoading;
        },
        setError: (state, action: PayloadAction<{ type: 'online' | 'offline'; error: string | null }>) => {
            state.error[action.payload.type] = action.payload.error;
        },
        addOfflineProject: (state, action: PayloadAction<Project>) => {
            state.offlineProjects.push(action.payload);
        },
        removeOfflineProject: (state, action: PayloadAction<number>) => {
            state.offlineProjects = state.offlineProjects.filter(project => project.id !== action.payload);
        },
        updateOfflineProject: (state, action: PayloadAction<Project>) => {
            const index = state.offlineProjects.findIndex(project => project.id === action.payload.id);
            if (index !== -1) {
                state.offlineProjects[index] = action.payload;
            }
        },
        resetProjects: (state) => {
            state.onlineProjects = [];
            state.offlineProjects = [];
            state.isAccordionOpen = false;
            state.activeTab = 'online';
            state.isLoading = { online: false, offline: false };
            state.error = { online: null, offline: null };
        }
    }
});

export const {
    setOnlineProjects,
    setOfflineProjects,
    toggleAccordion,
    setActiveTab,
    setLoading,
    setError,
    addOfflineProject,
    removeOfflineProject,
    updateOfflineProject,
    resetProjects
} = projectsSlice.actions;

export default projectsSlice.reducer;
